import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listClientGroups,
  createClientGroup,
  updateClientGroup,
  deleteClientGroup,
} from '../../services/adminClientGroupService';
import { ClientGroupDetail, ClientGroupManageRequest } from '../../types/adminClientGroup';

interface AdminClientGroupState {
  groups: ClientGroupDetail[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  actionMessage: string | null;
}

const initialState: AdminClientGroupState = {
  groups: [],
  loading: false,
  saving: false,
  error: null,
  actionMessage: null,
};

export const fetchGroups = createAsyncThunk(
  'adminClientGroups/fetch',
  async (adminApiKey: string) => listClientGroups(adminApiKey)
);

export const createGroupThunk = createAsyncThunk(
  'adminClientGroups/create',
  async ({ payload, adminApiKey }: { payload: ClientGroupManageRequest; adminApiKey: string }) =>
    createClientGroup(payload, adminApiKey)
);

export const updateGroupThunk = createAsyncThunk(
  'adminClientGroups/update',
  async ({
    id,
    payload,
    adminApiKey,
  }: {
    id: number;
    payload: ClientGroupManageRequest;
    adminApiKey: string;
  }) => updateClientGroup(id, payload, adminApiKey)
);

export const deleteGroupThunk = createAsyncThunk(
  'adminClientGroups/delete',
  async ({ id, adminApiKey }: { id: number; adminApiKey: string }) => {
    await deleteClientGroup(id, adminApiKey);
    return id;
  }
);

const upsert = (state: AdminClientGroupState, group: ClientGroupDetail) => {
  const index = state.groups.findIndex((g) => g.id === group.id);
  if (index >= 0) {
    state.groups[index] = group;
  } else {
    state.groups.push(group);
  }
};

const adminClientGroupSlice = createSlice({
  name: 'adminClientGroups',
  initialState,
  reducers: {
    clearGroupError: (state) => {
      state.error = null;
    },
    clearGroupMessage: (state) => {
      state.actionMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load client groups';
      })
      .addCase(createGroupThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(createGroupThunk.fulfilled, (state, action) => {
        state.saving = false;
        upsert(state, action.payload);
        state.actionMessage = `Client group '${action.payload.name}' created successfully`;
      })
      .addCase(createGroupThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to create client group';
      })
      .addCase(updateGroupThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(updateGroupThunk.fulfilled, (state, action) => {
        state.saving = false;
        upsert(state, action.payload);
        state.actionMessage = `Client group '${action.payload.name}' updated successfully`;
      })
      .addCase(updateGroupThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to update client group';
      })
      .addCase(deleteGroupThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.actionMessage = null;
      })
      .addCase(deleteGroupThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.groups = state.groups.filter((g) => g.id !== action.payload);
        state.actionMessage = 'Client group deleted successfully';
      })
      .addCase(deleteGroupThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message || 'Failed to delete client group';
      });
  },
});

export const { clearGroupError, clearGroupMessage } = adminClientGroupSlice.actions;
export default adminClientGroupSlice.reducer;
